import { createContext, useContext, useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Show, useUser, useClerk } from "@clerk/react";
import { CalendarDays, MessageCircle, Menu, X, Sparkles, Sun, Moon, WandSparkles } from "lucide-react";
import { useT, useSwitchLocale, LOCALES } from "@/lib/i18n";
import { pub, API } from "@/lib/api";
import { GuidePanel, WhatsAppPanel } from "@/components/Dock";
import { MusicButton, StayConnected, LINKS, useX } from "@/components/extras";
import { PresenceBadge, useS } from "@/components/homeExtras";

const ConfigCtx = createContext({});
export const useConfig = () => useContext(ConfigCtx);

function LangSwitch() {
  const { locale } = useT();
  const sw = useSwitchLocale();
  return (
    <div className="flex rounded-full border border-[var(--gold)] overflow-hidden text-xs" role="group" aria-label="Language">
      {LOCALES.map((l) => (
        <button key={l} onClick={() => sw(l)} aria-pressed={locale === l} data-testid={`lang-${l}`}
          className={`px-2 min-h-[40px] font-semibold uppercase ${locale === l ? "bg-[var(--gold)] text-[var(--forest-deep)]" : ""}`}>{l}</button>
      ))}
    </div>
  );
}

const THEME_LABELS = {
  en: ["Day", "Night", "Magic"], ro: ["Zi", "Noapte", "Magic"], es: ["Día", "Noche", "Magia"],
  fr: ["Jour", "Nuit", "Magie"], it: ["Giorno", "Notte", "Magia"], pt: ["Dia", "Noite", "Magia"],
};

function ThemeSwitch() {
  const { locale } = useT();
  const [theme, setTheme] = useState(() => localStorage.getItem("aa_theme") || "day");
  useEffect(() => { document.documentElement.dataset.theme = theme; localStorage.setItem("aa_theme", theme); window.dispatchEvent(new CustomEvent("aa-theme", { detail: theme })); }, [theme]);
  useEffect(() => { const h = (e) => setTheme(e.detail); window.addEventListener("aa-theme", h); return () => window.removeEventListener("aa-theme", h); }, []);
  const labels = THEME_LABELS[locale] || THEME_LABELS.en;
  const opts = [["day", Sun], ["night", Moon], ["magic", WandSparkles]];
  return (
    <div className="theme-switch flex rounded-full border border-[var(--gold)] p-[2px] gap-[2px]" role="group" aria-label="Theme" data-testid="theme-switch">
      {opts.map(([k, Icon], i) => (
        <button key={k} onClick={() => setTheme(k)} aria-pressed={theme === k} aria-label={labels[i]} title={labels[i]} data-testid={`theme-${k}`}
          className={`w-8 h-8 sm:w-9 sm:h-9 grid place-items-center rounded-full transition-colors ${theme === k ? "bg-[var(--gold)] text-[var(--forest-deep)]" : "hover:text-[var(--gold-light)]"}`}>
          <Icon size={16} aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}

function Header() {
  const { tl, lp } = useT();
  const [sessions, oracle, explore, peru, about, tarot, quiz, cal, lib, club] = tl("nav");
  const [signIn, mySanctuary, , , , signOut, admin] = tl("utility");
  const [book] = tl("actions");
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { signOut: doSignOut } = useClerk();
  useEffect(() => { setOpen(false); }, [pathname]);
  const links = [["/sessions", sessions], ["/oracle", oracle], ["/peru", peru], ["/about", about]];
  const s = useS();
  const exploreLinks = [["/soul-posts", s("wkH")], ["/love-tarot", tarot], ["/love-oracle", quiz], ["/calendar", cal], ["/library", lib], ["/club", club]];
  const cls = ({ isActive }) => `px-2 py-2 text-[15px] font-semibold hover:text-[var(--gold)] transition-colors ${isActive ? "text-[var(--gold-light)]" : ""}`;
  return (
    <header className="dark-zone bg-[var(--forest-deep)] text-[var(--ivory)] sticky top-0 z-40 border-b border-[rgba(198,167,108,.25)] shadow-[0_1px_0_rgba(107,74,147,.35)]">
      <div className="wrap 2xl:!max-w-[1440px] flex items-center justify-between h-[72px] gap-4">
        <Link to={lp("")} className="flex items-center gap-3 shrink-0" data-testid="nav-home" aria-label="Ayahuasca Angel">
          <img src="/assets/logo-main-96.png" alt="" aria-hidden="true" width="52" height="52" className="w-[52px] h-[52px] object-contain drop-shadow-[0_0_6px_rgba(227,204,160,0.35)]" data-testid="header-logo" />
          <span className="serif text-[24px] leading-none hidden min-[480px]:inline">Ayahuasca Angel</span>
        </Link>
        <nav className="hidden xl:flex items-center gap-3" aria-label="Main">
          {links.slice(0, 2).map(([p, l]) => <NavLink key={p} to={lp(p)} className={cls} data-testid={`nav${p.replace("/", "-")}`}>{l}</NavLink>)}
          <details className="relative group">
            <summary className="list-none cursor-pointer px-2 py-2 text-[15px] font-semibold hover:text-[var(--gold)]" data-testid="nav-explore">{explore}</summary>
            <div className="absolute top-full left-0 mt-2 bg-[var(--paper)] text-[var(--ink)] rounded-2xl p-2 shadow-xl min-w-[220px] border">
              {exploreLinks.map(([p, l]) => <Link key={p} to={lp(p)} onClick={(e) => e.currentTarget.closest("details")?.removeAttribute("open")} className="block px-4 py-2 rounded-xl hover:bg-[var(--lavender)]" data-testid={`nav${p.replace("/", "-")}`}>{l}</Link>)}
            </div>
          </details>
          {links.slice(2).map(([p, l]) => <NavLink key={p} to={lp(p)} className={cls} data-testid={`nav${p.replace("/", "-")}`}>{l}</NavLink>)}
        </nav>
        <div className="flex items-center gap-2">
          <MusicButton />
          <div className="hidden sm:block"><ThemeSwitch /></div>
          <div className="hidden 2xl:block"><LangSwitch /></div>
          <Show when="signed-out"><span className="hidden md:block"><Link to={lp("/sign-in")} className="btn btn-ghost btn-sm whitespace-nowrap" data-testid="nav-signin">{signIn}</Link></span></Show>
          <Show when="signed-in"><span className="hidden md:block"><Link to={lp("/account")} className="btn btn-ghost btn-sm whitespace-nowrap" data-testid="nav-account">{mySanctuary}</Link></span></Show>
          <Link to={lp("/book")} className="btn btn-gold btn-sm !px-3 sm:!px-[18px] whitespace-nowrap" data-testid="nav-book">{book}</Link>
          <button className="2xl:hidden p-2 min-w-[44px] min-h-[44px]" aria-expanded={open} aria-controls="mobile-menu" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">{open ? <X /> : <Menu />}</button>
        </div>
      </div>
      {open && (
        <nav id="mobile-menu" className="2xl:hidden wrap pb-6 grid gap-1" aria-label="Mobile">
          {[...links, ...exploreLinks].map(([p, l]) => <Link key={p} to={lp(p)} className="py-3 border-b border-[rgba(198,167,108,.2)] font-semibold" data-testid={`mnav${p.replace("/", "-")}`}>{l}</Link>)}
          <div className="sm:hidden pt-4 w-fit"><ThemeSwitch /></div>
          <div className="flex flex-wrap gap-3 items-center justify-between pt-4"><LangSwitch />
            <Show when="signed-out"><Link to={lp("/sign-in")} className="btn btn-ghost btn-sm" data-testid="mnav-signin">{signIn}</Link></Show>
            <Show when="signed-in"><Link to={lp("/account")} className="font-semibold">{mySanctuary}</Link><button onClick={() => doSignOut({ redirectUrl: lp("") })} className="text-sm">{signOut}</button></Show>
          </div>
          <Link to={lp("/admin")} className="text-xs opacity-60 pt-2">{admin}</Link>
        </nav>
      )}
    </header>
  );
}

function Footer({ openCookies }) {
  const { t, tl, lp } = useT();
  const x = useX();
  const legal = tl("legalLinks");
  const nav = tl("nav");
  const slugs = ["privacy", "cookies", "terms", "booking-policy", "shipping-returns", "accessibility"];
  return (
    <footer className="dark-zone bg-[var(--forest-deep)] text-[var(--line)] pt-20 pb-32">
      <div className="wrap grid gap-12 md:grid-cols-[1.4fr_1fr_1fr]">
        <div>
          <img src="/assets/logo-main-192.png" alt="Ayahuasca Angel emblem: an open eye with wings and leaf" width="96" height="96" className="w-24 h-24 mb-4" loading="lazy" />
          <p className="serif text-3xl text-[var(--ivory)] mb-3">{t("brandLine")}</p>
          <p className="text-sm max-w-[48ch]">{t("footerStatement")}</p>
          <p className="text-xs mt-4 max-w-[52ch] opacity-80">{t("serviceScope")}</p>
        </div>
        <ul className="grid gap-2 text-sm content-start">
          {[["/sessions", nav[0]], ["/oracle", nav[1]], ["/love-tarot", nav[5]], ["/love-oracle", nav[6]], ["/calendar", nav[7]], ["/library", nav[8]], ["/club", nav[9]], ["/contact", nav[10]], ["/faq", nav[11]]].map(([p, l]) => <li key={p}><Link to={lp(p)} className="hover:text-[var(--gold-light)]">{l}</Link></li>)}
          <li><Link to={lp("/donate")} className="hover:text-[var(--gold-light)]" data-testid="footer-donate">{x("footerMore")}</Link></li>
        </ul>
        <ul className="grid gap-2 text-sm content-start">
          {slugs.map((s, i) => <li key={s}><Link to={lp(`/${s}`)} className="hover:text-[var(--gold-light)]" data-testid={`footer-${s}`}>{legal[i]}</Link></li>)}
          <li><button onClick={openCookies} className="underline underline-offset-4" data-testid="footer-cookie-settings">{tl("cookieButtons")[6]}</button></li>
          <li><a href={LINKS.ytEN} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--gold-light)]" data-testid="footer-yt-en">YouTube (EN)</a></li>
          <li><a href={LINKS.ytRO} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--gold-light)]" data-testid="footer-yt-ro">YouTube (RO)</a></li>
          <li><a href={LINKS.pg} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--gold-light)]">Purple Garden</a></li>
          <li><a href={LINKS.po} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--gold-light)]">Purple Ocean</a></li>
          <li><a href="mailto:ayahuascaangelhealing@gmail.com" className="hover:text-[var(--gold-light)]" data-testid="footer-email">ayahuascaangelhealing@gmail.com</a></li>
          <li><a href="mailto:ayahuascaangel@yahoo.com" className="hover:text-[var(--gold-light)]" data-testid="footer-email-secondary">ayahuascaangel@yahoo.com</a></li>
          <li><a href={LINKS.wa} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--gold-light)]">WhatsApp +689 87 31 13 69</a></li>
        </ul>
      </div>
      <div className="wrap mt-12 text-xs flex flex-col items-start gap-2">
        <span className="opacity-60">© {new Date().getFullYear()} Ayahuasca Angel · {t("signoff")}{t("machineNote") && ` · ${t("machineNote")}`}</span>
        <span className="opacity-80">Web design &amp; development: <a href="https://www.nextsite.ro/" target="_blank" rel="noopener" className="font-semibold underline-offset-4 hover:underline hover:text-[var(--gold-light)]" data-testid="footer-nextsite">NEXTSITE</a></span>
      </div>
    </footer>
  );
}

export function useConsent() {
  const read = () => { try { return JSON.parse(localStorage.getItem("aa_consent")); } catch { return null; } };
  const [c, setC] = useState(read);
  useEffect(() => { const h = () => setC(read()); window.addEventListener("aa-consent", h); return () => window.removeEventListener("aa-consent", h); }, []);
  const save = (v) => { localStorage.setItem("aa_consent", JSON.stringify({ ...v, at: new Date().toISOString(), version: "draft-1" })); window.dispatchEvent(new Event("aa-consent")); };
  return [c, save];
}

function CookieBanner({ forceOpen, onClose }) {
  const { t, tl } = useT();
  const [consent, save] = useConsent();
  const [prefs, setPrefs] = useState(false);
  const [media, setMedia] = useState(consent?.media || false);
  const [accept, reject, choose, savePrefs, mediaL, analyticsL] = tl("cookieButtons");
  if (consent && !forceOpen) return null;
  const done = (v) => { save(v); onClose(); };
  return (
    <div role="dialog" aria-modal="false" aria-labelledby="cookie-title" className="fixed bottom-0 inset-x-0 z-[60] p-4 pointer-events-none" data-testid="cookie-banner">
      <div className="card max-w-[720px] mx-auto p-6 pointer-events-auto md:mr-24 lg:mx-auto">
        <h2 id="cookie-title" className="text-2xl mb-2">{t("cookieTitle")}</h2>
        <p className="text-sm text-[var(--muted)] mb-4">{t("cookieBody")}</p>
        {prefs && (
          <div className="grid gap-2 mb-4 text-sm">
            <label className="flex gap-2 items-center"><input type="checkbox" checked disabled /> Essential</label>
            <label className="flex gap-2 items-center"><input type="checkbox" checked={media} onChange={(e) => setMedia(e.target.checked)} data-testid="cookie-media" /> {mediaL}</label>
            <label className="flex gap-2 items-center opacity-60"><input type="checkbox" disabled /> {analyticsL}</label>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <button className="btn btn-forest btn-sm" onClick={() => done({ media: true, analytics: false })} data-testid="cookie-accept">{accept}</button>
          <button className="btn btn-forest btn-sm" onClick={() => done({ media: false, analytics: false })} data-testid="cookie-reject">{reject}</button>
          {prefs ? <button className="btn btn-ghost btn-sm" onClick={() => done({ media, analytics: false })} data-testid="cookie-save">{savePrefs}</button>
            : <button className="btn btn-ghost btn-sm" onClick={() => setPrefs(true)} data-testid="cookie-choose">{choose}</button>}
        </div>
      </div>
    </div>
  );
}

function SupportDock() {
  const { tl, lp } = useT();
  const [book, ask, wa] = tl("actions");
  const [panel, setPanel] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const triggers = useRef({});
  const { pathname } = useLocation();
  const hidden = /\/(cart|checkout|book|payment|session)(\/|$)/.test(pathname);
  const close = () => { const k = panel; setPanel(null); setTimeout(() => triggers.current[k]?.focus(), 0); };
  if (hidden && !panel) {
    return <div className="fixed z-50 right-4 md:right-6" style={{ bottom: "max(16px, env(safe-area-inset-bottom))" }} data-testid="support-dock"><PresenceBadge /></div>;
  }
  const Btn = ({ k, label, icon, onClick, to }) => {
    const p = { ref: (el) => { triggers.current[k] = el; }, "aria-label": label, title: label, "data-testid": `dock-${k}`, className: "dock-btn flex items-center gap-2 min-h-[48px] min-w-[48px] rounded-full bg-[var(--forest)] text-[var(--gold-light)] px-3 shadow-lg border border-[rgba(198,167,108,.4)]" };
    return to ? <Link to={to} {...p}>{icon}<span className="text-sm font-semibold md:sr-only">{label}</span></Link>
      : <button onClick={onClick} aria-expanded={panel === k} {...p}>{icon}<span className="text-sm font-semibold md:sr-only">{label}</span></button>;
  };
  const star = (
    <svg width="28" height="28" viewBox="0 0 32 32" aria-hidden="true"><path d="M14 2 L16.5 11.5 L26 14 L16.5 16.5 L14 26 L11.5 16.5 L2 14 L11.5 11.5 Z" fill="#E3CCA0" /><circle cx="26" cy="5" r="1.8" fill="#C6A76C" /><circle cx="28" cy="24" r="1.3" fill="#C6A76C" /></svg>
  );
  const actions = (
    <>
      <Btn k="guide" label={ask} icon={star} onClick={() => setPanel("guide")} />
      <Btn k="book" label={book} icon={<CalendarDays size={22} />} to={lp("/book")} />
      <Btn k="wa" label={wa} icon={<MessageCircle size={22} />} onClick={() => setPanel("wa")} />
    </>
  );
  return (
    <>
      <div className="fixed z-50 right-4 md:right-6 flex flex-col items-end gap-3" style={{ bottom: "max(16px, env(safe-area-inset-bottom))" }} data-testid="support-dock">
        <PresenceBadge />
        <div className="hidden md:flex flex-col gap-3">{actions}</div>
        <div className="md:hidden flex flex-col items-end gap-2">
          {mobileOpen && actions}
          <button className="dock-btn min-h-[52px] min-w-[52px] rounded-full bg-[var(--forest)] grid place-items-center shadow-lg border border-[var(--gold)]" aria-expanded={mobileOpen} aria-label="Help" onClick={() => setMobileOpen(!mobileOpen)} data-testid="dock-mobile-toggle">{mobileOpen ? <X color="#E3CCA0" /> : <Sparkles color="#E3CCA0" />}</button>
        </div>
      </div>
      {panel === "guide" && <GuidePanel onClose={close} />}
      {panel === "wa" && <WhatsAppPanel onClose={close} />}
    </>
  );
}

export default function Layout() {
  const [config, setConfig] = useState({});
  const [cookieOpen, setCookieOpen] = useState(false);
  const { t } = useT();
  useEffect(() => { pub.get("/public/config").then((r) => setConfig(r.data)).catch(() => setConfig({ error: true })); }, []);
  return (
    <ConfigCtx.Provider value={config}>
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] btn btn-gold">{t("skip")}</a>
      <Header />
      <main id="main" tabIndex={-1} className="pb-dock min-h-[60vh]"><Outlet /></main>
      <Footer openCookies={() => setCookieOpen(true)} />
      <SupportDock />
      <CookieBanner forceOpen={cookieOpen} onClose={() => setCookieOpen(false)} />
      <StayConnected />
    </ConfigCtx.Provider>
  );
}

export { API };
